import Handlebars from 'handlebars';
import advancedFormat from 'dayjs/plugin/advancedFormat.js';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { BrandingType } from '@dr-pam/common-types/database/client';
import CurrencyUtils from '../Utils/CurrencyUtils';
import DateTimeUtils from '../Utils/DateTimeUtils';
import type { SimpleAddress } from '../Utils/AddressUtils';
import { simpleAddressToString } from '../Utils/AddressUtils';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports -- used as a constructor parameter type; NestJS reads it from emitDecoratorMetadata, and a type-only import is erased so Nest fails at boot
import MarkdownService from './MarkdownService';

dayjs.extend(advancedFormat);

export type Branding = {
	name: string;
	email: string;
	website: string;
	legalEntity: string;
	businessNumber: string;
	logoSrc: string;
	showProgrammesInInvoice: boolean;
};

export default class TemplateRenderService {
	protected readonly _templates = new Map<string, HandlebarsTemplateDelegate<unknown>>();

	constructor(
		private readonly _branding: Map<BrandingType, Branding>,
		private readonly _markdownService: MarkdownService,
	) {
		Handlebars.registerHelper('dateFormat', this.helperDateFormat);
		Handlebars.registerHelper('timeFormat', this.helperTimeFormat);
		Handlebars.registerHelper('currency', this.helperCurrency);
		Handlebars.registerHelper('multiLineAddress', this.helperMultiLineAddress);
		Handlebars.registerHelper('nl2br', this.convertNewlinesToHtmlBreaks);
		Handlebars.registerHelper('extractFirstName', this.extractFirstName);
		Handlebars.registerHelper('mdQuote', this.mdQuote);
		Handlebars.registerHelper('splitRows', this.helperSplitRows);
		Handlebars.registerHelper('pluralise', this.helperPluralise);
	}

	public registerTemplateString(name: string, template: string) {
		this._templates.set(name, Handlebars.compile(template));
	}

	public compileAndRender<T>(template: string, data: T) {
		return Handlebars.compile(template)(data);
	}

	public render<T>(name: string, data: T) {
		const template = this._templates.get(name);
		if (!template) {
			throw new Error(`Template ${name} not registered`);
		}
		return template(data);
	}

	public async renderMarkdownAsHtml<T>(name: string, data: T) {
		const markdownSource = this.render(name, data);
		const rendered = await this._markdownService.markdownToHtml(markdownSource);
		return rendered.html;
	}

	public async compileAndRenderMarkdownAsHtml<T>(template: string, data: T) {
		const markdownSource = this.compileAndRender(template, data);
		const rendered = await this._markdownService.markdownToHtml(markdownSource);
		return rendered.html;
	}

	public getBranding(brandingIdentifier: BrandingType) {
		const branding = this._branding.get(brandingIdentifier);
		if (!branding) {
			throw new Error(`Branding ${brandingIdentifier} not registered`);
		}
		return branding;
	}

	private helperDateFormat(date: Date | Dayjs | string, format?: string) {
		if (typeof format !== 'string') {
			format = 'Do MMMM YYYY';
		}

		// A calendar day is printed as it is, not read as midnight in the server's zone.
		if (typeof date === 'string' && DateTimeUtils.parseCalendarDate(date)) {
			return DateTimeUtils.formatCalendarDate(date, format);
		}

		return dayjs(date).format(format);
	}

	private helperTimeFormat(date: Date | Dayjs | string, format?: string) {
		date = dayjs(date);

		if (typeof format !== 'string') {
			format = 'hh:mm A';
		}

		return date.format(format);
	}

	private helperCurrency(amount: number | string, currencyCode?: unknown) {
		if (typeof amount === 'string') {
			amount = parseFloat(amount);
		}
		// Handlebars appends its internal `options` object as the final argument even when the
		// template passes only the amount, so only treat a string second arg as the currency code.
		const resolvedCurrency = typeof currencyCode === 'string' ? currencyCode : undefined;
		return CurrencyUtils.format(amount, resolvedCurrency);
	}

	private helperMultiLineAddress(addr: string | SimpleAddress) {
		if (typeof addr === 'string') {
			return addr.replace(/,\s*/g, '<br/>');
		} else {
			return simpleAddressToString(addr, '<br/>');
		}
	}

	private convertNewlinesToHtmlBreaks(text: string) {
		text = Handlebars.Utils.escapeExpression(text);
		text = text.replace(/(\r\n|\n|\r)/gm, '<br>');
		return new Handlebars.SafeString(text);
	}

	private extractFirstName(text: string) {
		const parts = text.split(/\s+/);
		if (parts.length > 0) {
			return parts[0];
		} else {
			return text;
		}
	}

	private mdQuote(text: string) {
		return text.replace(/\n/g, '\n> ');
	}

	/**
	 * Splits a list into rows. `pattern` gives the row sizes for each length, separated by `|`:
	 * "1|2|2 1" puts three items in a row of two then one. A length it does not cover, or sizes that
	 * do not add up to it, give one row, so no item is ever dropped.
	 */
	private helperSplitRows(items: unknown, pattern: unknown) {
		if (!Array.isArray(items)) {
			return [];
		}
		let sizes =
			typeof pattern === 'string'
				? (pattern.split('|')[items.length - 1]?.trim().split(/\s+/).map(Number) ?? [])
				: [];
		const valid = sizes.every((size) => Number.isInteger(size) && size > 0);
		if (!valid || sizes.reduce((sum, size) => sum + size, 0) !== items.length) {
			sizes = [items.length];
		}
		const rows: unknown[][] = [];
		let start = 0;
		for (const size of sizes) {
			rows.push(items.slice(start, start + size));
			start += size;
		}
		return rows;
	}

	// Handlebars passes its options object in place of a missing argument.
	private helperPluralise(count: unknown, singular: unknown, plural: unknown) {
		const one = typeof singular === 'string' ? singular : '';
		return Number(count) === 1 || typeof plural !== 'string' ? one : plural;
	}
}

export enum CommonTemplate {
	USER_REQUEST_RESET = 'user_request_reset',
	INVOICE_PDF = 'invoice_pdf',
	INVOICE_EMAIL = 'invoice_email',
	SUBSCRIPTION_EXPIRING_SOON_EMAIL = 'subscription_expiring_soon_email',
	SUBSCRIPTION_EXPIRED_EMAIL = 'subscription_expired_email',
	SUBSCRIPTION_RENEWED_EMAIL = 'subscription_renewed_email',
	SUBSCRIPTION_RENEWAL_FAILED_EMAIL = 'subscription_renewal_failed_email',
	SUBSCRIPTION_CANCELLED_EMAIL = 'subscription_cancelled_email',
	EVENT_REGISTRATION_CONFIRMATION_EMAIL = 'event_registration_confirmation_email',
	EVENT_REGISTRATION_CANCELLED_EMAIL = 'event_registration_cancelled_email',
	CONTACT_FORM = 'contact_form',
}

export type CommonTemplateVars = {
	customer: {
		name: string;
		email: string;
		address?: SimpleAddress;
	};
	branding: Branding;
	brandingType: BrandingType;
};

export type CommonSubscriptionTemplateVars = CommonTemplateVars & {
	subscription: {
		automaticRenew: boolean;
		name: string;
		validTo: Dayjs | Date | string | number | null;
	};
};

export type CommonEventRegistrationTemplateVars = CommonTemplateVars & {
	event: {
		name: string;
		timezone: string;
		date: Dayjs | Date | string | number | null;
	};
};

export type SubscriptionExpiringSoonVars = CommonSubscriptionTemplateVars;
export type SubscriptionExpiredVars = CommonSubscriptionTemplateVars;
export type SubscriptionRenewedVars = CommonSubscriptionTemplateVars;
export type SubscriptionRenewalFailedVars = CommonSubscriptionTemplateVars;
export type SubscriptionCancelledVars = CommonSubscriptionTemplateVars;

export type EventRegistrationCancelledVars = CommonEventRegistrationTemplateVars;
export type EventRegistrationConfirmationVars = CommonEventRegistrationTemplateVars & {
	event: {
		description: string;
	};
};

export type InvoiceEmailVars = CommonTemplateVars;
export type WelcomeVars = CommonTemplateVars;

export type InvoicePdfVars = CommonTemplateVars & {
	isGstApplicable: boolean;
	/**
	 * ISO 4217 currency code the invoice amounts are denominated in (e.g. "AUD", "EUR").
	 * Named `currencyCode` rather than `currency` to avoid colliding with the registered
	 * `currency` Handlebars helper used in the template.
	 */
	currencyCode: string;
	date: Dayjs | Date | string | number;
	invoiceNumber: string;
	lines: Array<{
		description: string;
		subDescription?: string;
		exGst: number;
		inclGst: number;
	}>;
	subtotal: number;
	discount: number;
	gst: number;
	total: number;
};

export type UserRequestResetVars = CommonTemplateVars & {
	resetUrl: string;
};

export type ContactFormField = {
	name: string;
	value: string;
	isLong?: boolean;
};

export type ContactFormVars = {
	fields: ContactFormField[];
};
